
<template>
    <div class="unit" @click="onSelectUnit"
        :class="{
            selected: unitIsSelected,
            [toClassName(unit.state)]: unit.state,
            [toClassName(unit.type)]: unit.state,
            selectable: isSelectable,
            removable: removable,
            resupplyable: resupplyable,
            'reveal-on-hover': revealOnHover,
        }" v-if="unit">
        <img class="unit-img"
            :src="imgUrl" v-for="(_, i) of new Array(unit.quantity)"
            :key="`${unit.type}-img-${i}`"
            draggable="false"
        />
        <fa-icon class="btn-icon " v-if="recruitable" icon="coins"
            @click="onCommand('recruit')" />
        <fa-icon class="btn-icon flipped" v-if="canPass" icon="reply"
            @click="onCommand('pass')" />
        <fa-icon class="btn-icon" v-if="canClaimInitiative" icon="crown"
            @click="onCommand('claim_initiative')" />
        <div class="btn-icon-group bolster" v-if="bolsterable"
            @click="onCommand('bolster')">
            <fa-icon class="action-icon" icon="plus-square" />
            <icon class="action-icon-secondary" icon="knight-helmet" />
        </div>
        <div class="btn-icon-group attack" v-if="attackable"
            @click="onCommand('attack')">
                <icon class="action-icon" icon="sword" />
        </div>
        <div class="btn-icon-group attack sacrifice-attack" v-if="sacrificeAttackable"
            @click="onCommand('sacrifice_attack')">
                <icon class="action-icon" icon="sword" />
                <fa-icon class="action-icon drop" icon="tint" />
        </div>
        <fa-icon class="btn-icon remove" v-if="removable" icon="times"
            @click="onCommand('remove')" />
        <fa-icon class="btn-icon resupply" v-if="resupplyable" icon="heart"
            @click="onCommand('resupply')" />
        <div class="facedown-modal" v-if="revealOnHover">
            <img
                class="unit-img facedown-unit"
                v-for="(facedownUnit, i) of facedownUnits"
                :key="`facedown-unit-${facedownUnit.id}-${i}`"
                :src="`${apiUrl}/assets/${facedownUnit.imageName}`"
                draggable="false"
            />
        </div>
    </div>
</template>

<script>
import find from 'lodash.find';
import get from 'lodash.get';
import coinBackImg from '../../assets/coin_back.png';
import { API_BASE_URL } from '@/helpers/env';
import {
    UNIT_STATE_DEPLOYED,
    UNIT_STATE_DISCARDED_FACEDOWN,
    UNIT_STATE_IN_HAND,
    UNIT_TYPE_BISHOP,
} from './Unit-types';

export default {
    props: ['position', 'unit'],
    name: 'Unit',
    computed: {
        actions() {
            return this.actionsState.actions;
        },
        actionsState() {
            return this.$store.state.actionsState;
        },
        attackable() {
            return this.selectedUnitActions.filter(action =>
                action.type === 'attack' &&
                action.opponentUnitId === this.unit.id &&
                action.friendlyUnitId === this.friendlyUnitId,
            ).length;
        },
        sacrificeAttackable() {
            return this.selectedUnitActions.filter(action =>
                action.type === 'sacrifice_attack' &&
                action.opponentUnitId === this.unit.id &&
                action.friendlyUnitId === this.friendlyUnitId,
            ).length;
        },
        bolsterable() {
            let bolsterUnitIds = [];
            const bolsterActions = this.selectedUnitActions.filter(action => action.type === 'bolster');

            bolsterActions.map((action) => {
                bolsterUnitIds = bolsterUnitIds.concat(action.friendlyUnitIds);
            });

            return bolsterUnitIds.includes(this.unit.id);
        },
        canClaimInitiative() {
            return find(this.selectedUnitActions, { unitId: this.unit.id, type: 'claim_initiative' });
        },
        canPass() {
            return find(this.selectedUnitActions, { unitId: this.unit.id, type: 'pass' });
        },
        disabled() {
            return this.unit.playerId === this.player.id &&
                this.unit.state === UNIT_STATE_IN_HAND &&
                !find(this.actions, { unitId: this.unit.id }) &&
                !this.isSpectator;
        },
        friendlyUnitId() {
            return this.actionsState.friendlyUnitId;
        },
        gameId() {
            return parseInt(this.$route.params.id, 10);
        },
        gameState() {
            return this.$store.state.gameState;
        },
        apiUrl() {
            return API_BASE_URL;
        },
        imgUrl() {
            return this.unit.imageName ? `${this.apiUrl}/assets/${this.unit.imageName}` : coinBackImg;
        },
        isSelectable() {
            return this.actionsState.selectableUnitIds.includes(this.unit.id) &&
                !this.isSpectator;
        },
        isGame() {
            return this.$route.name === 'game';
        },
        player() {
            return this.isGame ? this.$store.getters.player : this.$store.getters.replayPlayer;
        },
        players() {
            return this.gameState.players;
        },
        recruitable() {
            const recruitAction = find(this.selectedUnitActions, { type: 'recruit' });
            return recruitAction && recruitAction.friendlyUnitIds.includes(this.unit.id);
        },
        selectedUnitActions() {
            return this.actionsState.selectedUnitActions;
        },
        unitIsSelected() {
            const { selectedUnitId, friendlyUnitId } = this.actionsState;

            return (selectedUnitId && selectedUnitId === this.unit.id) ||
                (friendlyUnitId && friendlyUnitId === this.unit.id);
        },
        userId() {
            return this.$store.state.loginState.userId;
        },
        isSpectator() {
            return this.$store.getters.isSpectator;
        },
        facedownUnits() {
            return this.gameState.facedownUnits;
        },
        revealOnHover() {
            return get(this.$parent, '$parent.showHand') &&
                this.unit.state === UNIT_STATE_DISCARDED_FACEDOWN &&
                this.facedownUnits &&
                this.facedownUnits.length;
        },
        removable() {
            return this.actionsState.removableUnitIds.includes(this.unit.id) &&
                !this.isSpectator;
        },
        resupplyable() {
            return this.actionsState.resupplyUnitIds.includes(this.unit.id) &&
                !this.isSpectator;
        },
        submittingAction() {
            return this.actionsState.submitting;
        },
    },
    methods: {
        async onCommand(actionType) {
            if (actionType === 'attack' || actionType === 'sacrifice_attack') {
                const attackAction = find(this.actionsState.selectedUnitActions, {
                    unitId: this.actionsState.selectedUnitId,
                    opponentUnitId: this.unit.id,
                });

                let targetPosition = this.actionsState.selectedPosition;

                if (!targetPosition || !attackAction.targetPositions.includes(targetPosition)) {
                    targetPosition = attackAction.targetPositions[0];
                }

                this.$store.dispatch('setOpponentUnitId', this.unit.id);

                if (targetPosition) {
                    this.$store.dispatch('setSelectedPosition', targetPosition);
                }
            } else {
                this.$store.dispatch('setSelectedPosition', null);
            }

            if (['bolster', 'recruit'].includes(actionType)) {
                this.$store.dispatch('setFriendlyUnitId', this.unit.id);
            }

            if (actionType === 'remove') {
                this.$store.dispatch('setSelectedUnitId', this.unit.id);
                this.$store.dispatch('setFriendlyUnitId', this.unit.id);
            }

            if (actionType === 'resupply') {
                this.$store.dispatch('setSelectedUnitId', this.unit.id);
                this.$store.dispatch('setFriendlyUnitId', this.unit.id);
            }

            await this.$store.dispatch('sendCommand',
                {
                    actionType,
                    gameId: this.gameId,
                    playerId: this.player.id,
                    $socket: this.$socket,
                    $notify: this.$notify,
                },
            );
        },
        onSelectUnit() {
            this.$store.dispatch('setSoundReady');

            const { unit } = this;

            if (unit.state === UNIT_STATE_IN_HAND &&
                this.player.id !== this.gameState.activePlayerId) {
                this.$notify({
                    group: 'app',
                    clean: true,
                });
                this.$notify({
                    group: 'app',
                    title: '',
                    type: 'info',
                    text: 'Please wait for your turn',
                });
                return;
            }

            if (!this.isSelectable) {
                return;
            }

            const friendlyUnitId = unit.state === UNIT_STATE_DEPLOYED ? unit.id : null;

            this.$store.dispatch('selectUnit', { unitId: unit.id, friendlyUnitId });

            if (unit.type === UNIT_TYPE_BISHOP &&
                unit.state === UNIT_STATE_IN_HAND) {
                if (this.$store.getters.deployedUnitTypes.includes(UNIT_TYPE_BISHOP)) {
                    this.$store.dispatch('setBishopRecruit', { value: true, notify: false });
                } else {
                    this.$store.dispatch('setBishopRecruit', { value: false, notify: false });
                }
            }
        },
        toClassName(text) {
            return text ? text.toLowerCase().replace(/_/g, '-') : '';
        },
        getUnitType(unitType) {
            return find(this.gameState.unitTypes, { type: unitType });
        },
    },
};
</script>


<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

    .unit {
        height: 100%;
        left: 50%;
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
        width: 100%;

        .unit-img {
            $leftIncrement: 3;
            $topIncrement: 8.5;
            border-radius: 50%;
            left: 50%;
            position: absolute;
            top: 50%;
            transform: translate(-50%, -50%);

            @for $i from 2 through 10 {
                &:nth-of-type(#{$i}) {
                    box-shadow: -2px 2px 1px -1px rgba(0,0,0,0.2);
                    left: #{50 + $leftIncrement + '%'};
                    top: #{50 - $topIncrement + '%'};
                    $leftIncrement: $leftIncrement + 3;
                    $topIncrement: $topIncrement + 8.5;
                }
            }

            &:after {
                box-shadow: -2px 3px 0px -1px #2e2d31;
                content: '';
                height: 110%;
                width: 110%;
            }
        }

        &.deployed {

            .unit-img {
                height: auto;
                width: 68%;
            }

            .btn-icon-group {
                user-select: none;

                &.bolster {
                    border-radius: 4px;
                    padding: 2px 4px;
                    position: absolute;
                    top: -5px;
                    right: -9px;

                    > * {
                        color: white;
                        fill: white;
                    }

                    .action-icon {
                        font-size: .7em;
                        margin-right: 3px;
                    }

                    .action-icon-secondary {
                        height: 14px;
                        width: 14px;
                    }
                }

                &.attack {
                    left: 50%;
                    position: absolute;
                    top: 50%;
                    transform: translate(-50%, -50%);

                    .action-icon {
                        width: 22px;
                        height: 22px;

                        &.drop {
                            position: absolute;
                            width: 9px;
                            height: 9px;
                            right: 3px;
                            top: 14px;
                        }
                    }
                }
            }
        }

        &.in-supply {

            .btn-icon {
                background: $burnished-gold;
                border: 1px solid $burnished-gold;
                bottom: -14px;
                position: absolute;
                right: -22px;
            }
        }

        &.eliminated {

            .btn-icon {
                bottom: -20px;
                position: absolute;
                right: -26px;
                height: 18px;
                width: 18px;
                max-height: 18px;
                max-width: 18px;

                &.resupply {
                    background: $lancer;
                }
            }
        }

        &.in-hand,
        &.deployed {

            &.selected {
                z-index: 2;
            }

            .btn-icon {
                position: absolute;
                bottom: -14px;
            }

            .btn-icon:first-of-type {
                left: -22px;
            }

            .btn-icon:nth-of-type(2) {
                left: auto;
                right: -22px;
            }
        }

        &.removable {

            .remove {

                &.btn-icon:first-of-type {
                    height: 18px;
                    width: 18px;
                    left: 50%;
                    position: absolute;
                    top: 50%;
                    transform: translate(-50%, -50%);
                }
            }
        }

        &.selected {

            .unit-img {
                &:first-of-type {
                    border: 3px outset $gray;
                }
            }

            &.archer {
                .unit-img {
                    &:first-of-type {
                        border-color: $archer;
                    }
                }
            }

            &.bannerman {
                .unit-img {
                    &:first-of-type {
                        border-color: $bannerman;
                    }
                }
            }

            &.berserker {
                .unit-img {
                    &:first-of-type {
                        border-color: $berserker;
                    }
                }
            }

            &.bishop {
                .unit-img {
                    &:first-of-type {
                        border-color: $bishop;
                    }
                }
            }

            &.cavalry {
                .unit-img {
                    &:first-of-type {
                        border-color: $cavalry;
                    }
                }
            }

            &.crossbowman {

                .unit-img {
                    &:first-of-type {
                        border-color: $crossbowman;
                    }
                }
            }

            &.ensign {
                .unit-img {
                    &:first-of-type {
                        border-color: $ensign;
                    }
                }
            }

            &.earl {
                .unit-img {
                    &:first-of-type {
                        border-color: $earl;
                    }
                }
            }

            &.footman {
                .unit-img {
                    &:first-of-type {
                        border-color: $footman;
                    }
                }
            }

            &.herald {
                .unit-img {
                    &:first-of-type {
                        border-color: $herald;
                    }
                }
            }

            &.knight {
                .unit-img {
                    &:first-of-type {
                        border-color: $knight;
                    }
                }
            }

            &.lancer {
                .unit-img {
                    &:first-of-type {
                        border-color: $lancer;
                    }
                }
            }

            &.light-cavalry {
                .unit-img {
                    &:first-of-type {
                        border-color: $light-cavalry;
                    }
                }
            }

			&.corsair {
                .unit-img {
                    &:first-of-type {
                        border-color: $corsair;
                    }
                }
            }

            &.marshal {
                .unit-img {
                    &:first-of-type {
                        border-color: $marshal;
                    }
                }
            }

            &.mercenary {
                .unit-img {
                    &:first-of-type {
                        border-color: $mercenary;
                    }
                }
            }

            &.pikeman {
                .unit-img {
                    &:first-of-type {
                        border-color: $pikeman;
                    }
                }
            }

            &.pirate {
                .unit-img {
                    &:first-of-type {
                        border-color: $pirate;
                    }
                }
            }

            &.royal-coin-black {
                .unit-img {
                    &:first-of-type {
                        border-color: $royal-coin-black;
                    }
                }
            }

            &.royal-coin-white {
                .unit-img {
                    &:first-of-type {
                        border-color: $royal-coin-white;
                    }
                }
            }

            &.royal-guard,
            &.royal-guard-nobility {
                .unit-img {
                    &:first-of-type {
                        border-color: $royal-guard;
                    }
                }
            }

            &.sapper {
                .unit-img {
                    &:first-of-type {
                        border-color: $sapper;
                    }
                }
            }

            &.scout {
                .unit-img {
                    &:first-of-type {
                        border-color: $scout;
                    }
                }
            }

            &.siege-tower {
                .unit-img {
                    &:first-of-type {
                        border-color: $siege-tower;
                    }
                }
            }

            &.swordsman {
                .unit-img {
                    &:first-of-type {
                        border-color: $swordsman;
                    }
                }
            }

            &.trebuchet {
                .unit-img {
                    &:first-of-type {
                        border-color: $trebuchet;
                    }
                }
            }

            &.warrior-priest {
                .unit-img {
                    &:first-of-type {
                        border-color: $warrior-priest;
                    }
                }
            }

            &.war-wagon {
                .unit-img {
                    &:first-of-type {
                        border-color: $war-wagon;
                    }
                }
            }
        }

        &.selectable {
            cursor: pointer;
        }

        &.disabled {
            opacity: .5;
            cursor: default;
        }

        &.discarded-facedown {

            &.reveal-on-hover {
                &:hover {

                    .unit-img {
                        visibility: hidden;
                    }

                    .facedown-unit {
                        visibility: visible;
                    }

                    .facedown-modal {
                        display: block;
                    }
                }
            }
        }

        .facedown-modal {
            display: none;
            height: 100%;
            width: 100%;
            bottom: 0;
            position: absolute;
        }

        .facedown-unit {
            $topIncrement: 12.5;
            $leftIncrement: 2.5;
            position: absolute;
            bottom: 0;
            left: 0;

            @for $i from 2 through 10 {
                &:nth-of-type(#{$i}) {
                    position: absolute;
                    box-shadow: -2px 2px 1px -1px rgba(0,0,0,0.2);
                    top: -#{$topIncrement}px;
                    left: #{$leftIncrement}px;
                    $topIncrement: $topIncrement + 12.5;
                    $leftIncrement: $leftIncrement + 2.5;
                }
            }
        }

        .unit-count {
            background: white;
            border-radius: 50%;
            display: flex;
            font-size: .9em;
            justify-content: center;
            opacity: .9;
            padding: 0px 5px;
            position: absolute;
            right: -5px;
            top: 12px;
            transform: translate(-50%, -50%);
        }
    }

    .replay {

        .unit {

            &.disabled {
                opacity: 1;
            }
        }
    }

    .mobile-player-mat {

        .mat-content {

            .mat-options {

                &.mat-options-center {

                    .unit {
                        left: auto;
                        position: relative;
                        top: auto;
                        transform: none;
                        width: 25%;
                        margin: 0 2%;
                        border-radius: 50%;

                        &:first-of-type {
                            margin-left: 0;
                        }

                        &:last-of-type {
                            margin-right: 0;
                        }

                        .unit-img {
                            align-self: center;
                            height: auto;
                            position: relative;
                            left: auto;
                            top: auto;
                            transform: none;
                            width: 100%;
                        }
                    }

                    .units-group {

                            &.units-in-supply {

                                .unit {
                                     width: 20%;
                                }
                            }

                            .unit {

                                .unit-img {
                                    $leftIncrement: 3;
                                    $topIncrement: 8.5;
                                    border-radius: 50%;
                                    height: auto;
                                    left: 50%;
                                    position: absolute;
                                    top: 50%;
                                    transform: translate(-50%, -50%);
                                    width: 100%;

                                    @for $i from 2 through 10 {
                                        &:nth-of-type(#{$i}) {
                                            box-shadow: -2px 2px 1px -1px rgba(0,0,0,0.2);
                                            left: #{50 + $leftIncrement + '%'};
                                            top: #{50 - $topIncrement + '%'};
                                            $leftIncrement: $leftIncrement + 3;
                                            $topIncrement: $topIncrement + 8.5;
                                        }
                                    }
                                }
                            }
                    }

                }
            }
        }
    }

</style>
